/**
 * RightRail – Collapsible right panel for Ask AI.
 *
 * Shows:
 *  - Mode used, Confidence gauge
 *  - Sources grouped: Internal (I1..In) + Web (W1..Wn)
 *  - Low-confidence helper callout
 *  - Domain-diversity warning
 *  - Action buttons: Copy citations, Open all web links, Download evidence pack
 *
 * Scrolls independently from the center pane.
 */
import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Divider,
    Button,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Alert,
    AlertTitle,
    Skeleton,
} from '@mui/material';
import {
    ChevronRight,
    Storage,
    TravelExplore,
    Language,
    Description,
    OpenInNew,
    ContentCopy,
    Download,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Info,
    Link as LinkIcon,
    Shield,
    Speed,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import PriceMovePanel from './PriceMovePanel';

/* ── Domain-diversity helper ── */
const getDomainDiversityWarning = (webSources) => {
    if (!webSources || webSources.length < 3) return null;
    const domainCounts = {};
    webSources.forEach((s) => {
        try {
            const domain = new URL(s.url || '').hostname;
            domainCounts[domain] = (domainCounts[domain] || 0) + 1;
        } catch { /* ignore bad URLs */ }
    });
    const total = webSources.length;
    const maxCount = Math.max(...Object.values(domainCounts), 0);
    if (maxCount / total > 0.6) {
        const topDomain = Object.entries(domainCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
        return `Low domain diversity — ${Math.round((maxCount / total) * 100)}% from ${topDomain}`;
    }
    return null;
};

/* ── Confidence display helpers ── */
const confidenceConfig = {
    HIGH: { label: 'High', color: 'var(--success)', bg: 'var(--bg-3)', icon: <CheckCircle sx={{ fontSize: 16, color: 'var(--success)' }} /> },
    MED: { label: 'Medium', color: 'var(--warning)', bg: 'var(--bg-3)', icon: <Warning sx={{ fontSize: 16, color: 'var(--warning)' }} /> },
    LOW: { label: 'Low', color: 'var(--danger)', bg: 'var(--bg-3)', icon: <ErrorIcon sx={{ fontSize: 16, color: 'var(--danger)' }} /> },
};

const getConfidenceFromNumber = (val) => {
    if (val >= 0.7) return 'HIGH';
    if (val >= 0.55) return 'MED';
    return 'LOW';
};

/* ── Mode display ── */
const modeConfig = {
    INTERNAL: { label: 'Internal', color: 'var(--brand-2)', bg: 'var(--bg-3)', icon: <Storage sx={{ fontSize: 15 }} /> },
    WEB: { label: 'Web', color: 'var(--warning)', bg: 'var(--bg-3)', icon: <Language sx={{ fontSize: 15 }} /> },
    HYBRID: { label: 'Hybrid', color: 'var(--brand-2)', bg: 'var(--bg-3)', icon: <TravelExplore sx={{ fontSize: 15 }} /> },
};

const RightRail = ({
    lastMessage,
    isLoading,
    onSourceClick,
    onClose,
    isMobile,
}) => {
    const sources = useMemo(() => lastMessage?.sources || [], [lastMessage]);
    const citations = lastMessage?.citations || [];
    const modeUsed = lastMessage?.modeUsed || null;
    const confidence = lastMessage?.confidence;
    const selfCheck = lastMessage?.selfCheck;
    const latencyMs = lastMessage?.latencyMs;
    const limitations = lastMessage?.limitations;

    const internalSources = useMemo(() => sources.filter((s) => s.sourceType === 'internal'), [sources]);
    const webSources = useMemo(() => sources.filter((s) => s.sourceType === 'web'), [sources]);
    // v1 internal citations
    const v1Citations = citations || [];

    const domainWarning = useMemo(() => getDomainDiversityWarning(webSources), [webSources]);

    const confKey = confidence != null
        ? (typeof confidence === 'number' ? getConfidenceFromNumber(confidence) : confidence)
        : null;
    const conf = confKey ? confidenceConfig[confKey] || confidenceConfig.LOW : null;
    const mode = modeUsed ? modeConfig[modeUsed] || modeConfig.INTERNAL : null;

    const hasAnySources = sources.length > 0 || v1Citations.length > 0;

    /* ── Actions ── */
    const copyCitations = () => {
        const lines = sources.map((s) => {
            const isW = s.sourceType === 'web';
            return `[${s.refId}] ${s.title}${isW && s.url ? ' — ' + s.url : ''}`;
        });
        if (v1Citations.length > 0) {
            v1Citations.forEach((c, idx) => {
                const fy = c.fiscal_year || c.fiscalYear || '?';
                lines.push(`[${idx + 1}] ${c.document_type || c.documentType || 'doc'} FY${fy}`);
            });
        }
        navigator.clipboard.writeText(lines.join('\n'));
        toast.success('Citations copied', { autoClose: 1200 });
    };

    const openAllWebLinks = () => {
        webSources.forEach((s) => {
            if (s.url) window.open(s.url, '_blank');
        });
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--bg-2)',
                borderLeft: isMobile ? 'none' : '1px solid var(--border)',
                overflow: 'hidden',
            }}
        >
            {/* ── Header ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid var(--border)',
                    minHeight: 48,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--text-1)', fontSize: '0.82rem', letterSpacing: '0.03em' }}>
                    SOURCES & META
                </Typography>
                {isMobile && (
                    <IconButton size="small" onClick={onClose}>
                        <ChevronRight fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* ── Scrollable body ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
                {/* Loading skeleton */}
                {isLoading && (
                    <Box>
                        <Skeleton variant="rounded" height={36} sx={{ mb: 1.5, borderRadius: 2 }} />
                        <Skeleton variant="rounded" height={36} sx={{ mb: 1.5, borderRadius: 2 }} />
                        <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
                        <Skeleton variant="rounded" height={28} sx={{ mb: 1, borderRadius: 1 }} />
                        <Skeleton variant="rounded" height={28} sx={{ mb: 1, borderRadius: 1 }} />
                        <Skeleton variant="rounded" height={28} sx={{ mb: 1, borderRadius: 1 }} />
                    </Box>
                )}

                {!isLoading && !lastMessage && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Info sx={{ color: 'var(--muted)', fontSize: 32, mb: 1 }} />
                        <Typography variant="body2" sx={{ color: 'var(--text-2)', fontSize: '0.82rem' }}>
                            Ask a question to see sources and metadata here.
                        </Typography>
                    </Box>
                )}

                {!isLoading && lastMessage && (
                    <>
                        {/* ── Mode + Confidence row ── */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            {mode && (
                                <Chip
                                    size="small"
                                    icon={mode.icon}
                                    label={mode.label}
                                    sx={{
                                        height: 26,
                                        fontSize: '0.73rem',
                                        fontWeight: 600,
                                        backgroundColor: mode.bg,
                                        color: mode.color,
                                        border: 'none',
                                    }}
                                />
                            )}
                            {conf && (
                                <Chip
                                    size="small"
                                    icon={conf.icon}
                                    label={`${conf.label} confidence`}
                                    sx={{
                                        height: 26,
                                        fontSize: '0.73rem',
                                        fontWeight: 600,
                                        backgroundColor: conf.bg,
                                        color: conf.color,
                                        border: 'none',
                                    }}
                                />
                            )}
                        </Box>

                        {/* ── Latency + self-check ── */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {latencyMs && (
                                <Chip
                                    size="small"
                                    icon={<Speed sx={{ fontSize: 14 }} />}
                                    label={`${(latencyMs / 1000).toFixed(1)}s`}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.68rem' }}
                                />
                            )}
                            {selfCheck && (
                                <Chip
                                    size="small"
                                    icon={selfCheck.passed ? <CheckCircle sx={{ fontSize: 14 }} /> : <Warning sx={{ fontSize: 14 }} />}
                                    label={`${selfCheck.citationCount} citations verified`}
                                    color={selfCheck.passed ? 'success' : 'warning'}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.68rem' }}
                                />
                            )}
                        </Box>

                        {lastMessage.priceMove && (
                            <PriceMovePanel priceMove={lastMessage.priceMove} />
                        )}

                        {/* ── Low-confidence helper ── */}
                        {confKey === 'LOW' && (
                            <Alert
                                severity="info"
                                icon={<Shield sx={{ fontSize: 18 }} />}
                                sx={{
                                    mb: 2,
                                    fontSize: '0.76rem',
                                    py: 0.5,
                                    '& .MuiAlert-message': { fontSize: '0.76rem' },
                                }}
                            >
                                <AlertTitle sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Low evidence coverage</AlertTitle>
                                Try: (a) Switch to <strong>Web</strong> or <strong>Hybrid</strong> mode
                                (b) Ask a more specific question
                                (c) Restrict to trusted domains <em>(coming soon)</em>
                            </Alert>
                        )}

                        {/* ── Domain-diversity warning ── */}
                        {domainWarning && (
                            <Alert severity="warning" sx={{ mb: 2, fontSize: '0.76rem', py: 0.5, '& .MuiAlert-message': { fontSize: '0.76rem' } }}>
                                {domainWarning}
                            </Alert>
                        )}

                        {/* ── Limitations ── */}
                        {limitations && (
                            <Alert severity="info" variant="outlined" sx={{ mb: 2, fontSize: '0.76rem', py: 0.5, '& .MuiAlert-message': { fontSize: '0.76rem' } }}>
                                {limitations}
                            </Alert>
                        )}

                        <Divider sx={{ mb: 1.5 }} />

                        {/* ── Source counts ── */}
                        <Typography
                            variant="overline"
                            sx={{ display: 'block', color: 'var(--muted)', fontSize: '0.63rem', letterSpacing: '0.08em', mb: 0.75 }}
                        >
                            Sources ({internalSources.length + webSources.length + v1Citations.length})
                        </Typography>

                        {/* ── Internal sources (v2) ── */}
                        {internalSources.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" sx={{ color: 'var(--brand-2)', fontWeight: 600, fontSize: '0.7rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Storage sx={{ fontSize: 13 }} /> Internal ({internalSources.length})
                                </Typography>
                                <List dense disablePadding>
                                    {internalSources.map((s) => (
                                        <ListItemButton
                                            key={s.refId}
                                            onClick={() => onSourceClick(s)}
                                            sx={{
                                                borderRadius: 1.5,
                                                py: 0.4,
                                                px: 1,
                                                mb: 0.25,
                                                border: '1px solid var(--border)',
                                                backgroundColor: 'var(--bg-3)',
                                                '&:hover': { backgroundColor: 'var(--row-hover)' },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 30 }}>
                                                <Chip
                                                    label={s.refId}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.65rem',
                                                        fontWeight: 700,
                                                        backgroundColor: 'var(--bg-2)',
                                                        color: 'var(--brand-2)',
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={s.title}
                                                secondary={[s.sectionName, s.pageRef].filter(Boolean).join(' · ')}
                                                primaryTypographyProps={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-1)' }}
                                                secondaryTypographyProps={{ fontSize: '0.66rem' }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Box>
                        )}

                        {/* ── Web sources (v2) ── */}
                        {webSources.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" sx={{ color: 'var(--warning)', fontWeight: 600, fontSize: '0.7rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Language sx={{ fontSize: 13 }} /> Web ({webSources.length})
                                </Typography>
                                <List dense disablePadding>
                                    {webSources.map((s) => {
                                        let domain = '';
                                        try { domain = new URL(s.url || '').hostname.replace('www.', ''); } catch { /* ignore */ }
                                        return (
                                            <ListItemButton
                                                key={s.refId}
                                                onClick={() => onSourceClick(s)}
                                                sx={{
                                                    borderRadius: 1.5,
                                                    py: 0.4,
                                                    px: 1,
                                                    mb: 0.25,
                                                    border: '1px solid var(--border)',
                                                    backgroundColor: 'var(--bg-3)',
                                                    '&:hover': { backgroundColor: 'var(--row-hover)' },
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 30 }}>
                                                    <Chip
                                                        label={s.refId}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                            fontWeight: 700,
                                                            backgroundColor: 'var(--bg-2)',
                                                            color: 'var(--warning)',
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {/* Favicon placeholder */}
                                                            <Box
                                                                component="img"
                                                                src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
                                                                alt=""
                                                                sx={{ width: 14, height: 14, borderRadius: '2px' }}
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                            <Typography component="span" sx={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-1)' }}>
                                                                {(s.title || domain).substring(0, 45)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography component="span" sx={{ fontSize: '0.64rem', color: 'var(--muted)' }}>
                                                            {domain}
                                                        </Typography>
                                                    }
                                                />
                                                <Tooltip title="Open link">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); window.open(s.url, '_blank'); }}
                                                        sx={{ ml: 0.5, p: 0.25 }}
                                                    >
                                                        <OpenInNew sx={{ fontSize: 13, color: 'var(--muted)' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {/* ── v1 internal citations (legacy) ── */}
                        {v1Citations.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" sx={{ color: 'var(--brand-2)', fontWeight: 600, fontSize: '0.7rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Description sx={{ fontSize: 13 }} /> Document Citations ({v1Citations.length})
                                </Typography>
                                <List dense disablePadding>
                                    {v1Citations.map((c, idx) => {
                                        const docType = c.document_type || c.documentType || 'doc';
                                        const fy = c.fiscal_year || c.fiscalYear || '?';
                                        const section = c.section_name || c.sectionName || '';
                                        const blobUrl = c.blob_url || c.blobUrl;
                                        return (
                                            <ListItemButton
                                                key={idx}
                                                onClick={() => {
                                                    if (blobUrl && !blobUrl.startsWith('embedded://')) {
                                                        window.open(blobUrl, '_blank');
                                                    }
                                                }}
                                                sx={{
                                                    borderRadius: 1.5,
                                                    py: 0.4,
                                                    px: 1,
                                                    mb: 0.25,
                                                    border: '1px solid var(--border)',
                                                    backgroundColor: 'var(--bg-3)',
                                                    '&:hover': { backgroundColor: 'var(--row-hover)' },
                                                    cursor: blobUrl && !blobUrl.startsWith('embedded://') ? 'pointer' : 'default',
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 28 }}>
                                                    <Chip
                                                        label={idx + 1}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                            fontWeight: 700,
                                                            backgroundColor: 'var(--bg-2)',
                                                            color: 'var(--brand-2)',
                                                            minWidth: 22,
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${docType === 'annual_report' ? 'Annual Report' : docType === 'earnings_call' ? 'Earnings Call' : docType} FY${fy}`}
                                                    secondary={section.length > 40 ? section.substring(0, 37) + '...' : section}
                                                    primaryTypographyProps={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-1)' }}
                                                    secondaryTypographyProps={{ fontSize: '0.66rem' }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {!hasAnySources && (
                            <Typography variant="body2" sx={{ color: 'var(--muted)', fontSize: '0.8rem', textAlign: 'center', py: 2 }}>
                                No sources for this answer.
                            </Typography>
                        )}
                    </>
                )}
            </Box>

            {/* ── Actions footer ── */}
            {hasAnySources && !isLoading && (
                <Box
                    sx={{
                        px: 2,
                        py: 1.25,
                        borderTop: '1px solid var(--border)',
                        display: 'flex',
                        gap: 0.75,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        size="small"
                        startIcon={<ContentCopy sx={{ fontSize: 13 }} />}
                        onClick={copyCitations}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'var(--text-2)' }}
                    >
                        Copy citations
                    </Button>
                    {webSources.length > 0 && (
                        <Button
                            size="small"
                            startIcon={<LinkIcon sx={{ fontSize: 13 }} />}
                            onClick={openAllWebLinks}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'var(--text-2)' }}
                        >
                            Open all links
                        </Button>
                    )}
                    <Tooltip title="Coming soon">
                        <span>
                            <Button
                                size="small"
                                startIcon={<Download sx={{ fontSize: 13 }} />}
                                disabled
                                sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                            >
                                Evidence pack
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default RightRail;
